import { useCallback, useMemo } from 'react';
import { useUpdateEditorThreadMutation } from 'src/store/services';
import {
  useGetUser,
  useToast,
  useEditorAutoSavingStatus,
  useErrorToast,
  useGetEditorThread,
} from 'src/hooks';

export const useEditorTitleRename = () => {
  const { onShowToast } = useToast();
  const { onShowErrorToast } = useErrorToast();
  const { user_id } = useGetUser();
  const { editorThread } = useGetEditorThread();
  const { onChangeAutoSavingStatus } = useEditorAutoSavingStatus();
  const [updateEditorThread, { isLoading: isRenameLoading }] =
    useUpdateEditorThreadMutation();

  const onTitleRename = useCallback(
    async ({ newTitle }: { newTitle: string }) => {
      onChangeAutoSavingStatus('loading');

      try {
        const result = await updateEditorThread({
          user_id,
          thread_id: editorThread?.thread_id || '',
          thread: {
            ...editorThread,
            thread_title: newTitle,
          },
        }).unwrap();

        if (result) {
          onChangeAutoSavingStatus('success');
          onShowToast('File renamed', 'success');
        }
      } catch (error) {
        onChangeAutoSavingStatus('error');
        onShowErrorToast();
      } finally {
        onChangeAutoSavingStatus(null);
      }
    },
    [
      editorThread,
      onChangeAutoSavingStatus,
      onShowErrorToast,
      onShowToast,
      updateEditorThread,
      user_id,
    ],
  );

  return useMemo(
    () => ({ onTitleRename, isRenameLoading }),
    [onTitleRename, isRenameLoading],
  );
};
